import { ViewerConfig } from '@/components/Viewer'
import type { CodeEditorMode } from '@/components/editor/code-editor'
import { useDispatch } from 'react-redux'

export type CodeEditorHandle = {
  mode: CodeEditorMode
  getValue: () => string
  /**
   * Notifies when the value has settled: the editor lost focus, or a new value
   * was applied from elsewhere. Returns an unsubscriber.
   */
  onSettled: (handler: () => void) => () => void
}

export type CurrentViewerState = {
  appCss: Record<string, string | number>
  activeCodeEditors: Record<string, CodeEditorHandle>
  votersForGlobalUserActionsDisabled: Set<string>
  config?: ViewerConfig | null
}

const initialState: CurrentViewerState = {
  appCss: {},
  activeCodeEditors: {},
  votersForGlobalUserActionsDisabled: new Set<string>(),
}

type Action
  = {type: "VOTE_USER_ACTIONS_DISABLED", data: {value: boolean, voterId: string}}
  | {type: "SET_VIEWER_CONFIG", data: ViewerConfig }
  | {type: "SET_APP_CSS", data?: Record<string,string>}
  | {type: "ADD_CODE_EDITOR", data: {id: string, editor: CodeEditorHandle}}
  | {type: "REMOVE_CODE_EDITOR", data: string}

export default function currentViewerReducer(state: CurrentViewerState = initialState, action: Action): CurrentViewerState {
  switch (action.type) {
    case "ADD_CODE_EDITOR": {
      const { id, editor } = action.data
      return {
        ...state,
        activeCodeEditors: {
          ...state.activeCodeEditors,
          [id]: editor
        }
      }
    }
    case 'REMOVE_CODE_EDITOR': {
      const remaining = { ...state.activeCodeEditors }
      delete remaining[action.data]
      return { ...state, activeCodeEditors: remaining }
    }
    case "SET_APP_CSS":
      return {...state, appCss: action.data ?? {}}
    case "SET_VIEWER_CONFIG":
      return {...state, config: action.data }
    case "VOTE_USER_ACTIONS_DISABLED":
      const newVoters = new Set(state.votersForGlobalUserActionsDisabled)
      if (action.data.value)
        newVoters.add(action.data.voterId)
      else
        newVoters.delete(action.data.voterId)

      return { ...state, votersForGlobalUserActionsDisabled: newVoters }
  }

  return state
}

export function isUserActionsDisabled(state: CurrentViewerState) {
  return state.votersForGlobalUserActionsDisabled.size > 0
}

export function useVoteToDisableUserActions() {
  const dispatch = useDispatch()
  return function voteToDisableUserActions(voterId: string, value: boolean) {
    dispatch({
      type:'VOTE_USER_ACTIONS_DISABLED',
      data: {value, voterId},
    } satisfies Action)
  }
}
